/**
 * Coordinate utilities for TrophyCoin Form Wizard
 * Supports Decimal Degrees (DD), Degrees Minutes Seconds (DMS), and UTM.
 */

/**
 * Format latitude and longitude numbers into standard DD string.
 * e.g. "-17.118579, 24.054117"
 */
export function formatDD(lat, lng) {
  if (lat === null || lat === undefined || lng === null || lng === undefined) return '';
  const numLat = Number(lat);
  const numLng = Number(lng);
  if (isNaN(numLat) || isNaN(numLng)) return '';
  return `${numLat.toFixed(6)}, ${numLng.toFixed(6)}`;
}

/**
 * Format latitude and longitude numbers into DMS string.
 * e.g. "17°07'06.9\"S, 24°03'14.8\"E"
 */
export function formatDMS(lat, lng) {
  if (lat === null || lat === undefined || lng === null || lng === undefined) return '';
  const numLat = Number(lat);
  const numLng = Number(lng);
  if (isNaN(numLat) || isNaN(numLng)) return '';

  const toDms = (val, isLat) => {
    const dir = isLat ? (val >= 0 ? 'N' : 'S') : (val >= 0 ? 'E' : 'W');
    const abs = Math.abs(val);
    const deg = Math.floor(abs);
    const minFloat = (abs - deg) * 60;
    const min = Math.floor(minFloat);
    const sec = ((minFloat - min) * 60).toFixed(1);
    return `${deg}°${min.toString().padStart(2, '0')}'${sec.padStart(4, '0')}"${dir}`;
  };

  return `${toDms(numLat, true)}, ${toDms(numLng, false)}`;
}

/**
 * Convert WGS84 latitude and longitude to UTM forward projection.
 * e.g. "35K 186514 8104953"
 */
export function toUTM(lat, lon) {
  if (lat === null || lat === undefined || lon === null || lon === undefined) return '';
  const numLat = Number(lat);
  const numLon = Number(lon);
  if (isNaN(numLat) || isNaN(numLon)) return '';

  const a = 6378137.0; // WGS84 semi-major axis
  const f = 1 / 298.257223563; // WGS84 flattening
  const e2 = 2 * f - f * f; // eccentricity squared
  const ePrime2 = e2 / (1 - e2);

  const radLat = (numLat * Math.PI) / 180;
  const radLon = (numLon * Math.PI) / 180;

  const zoneNumber = Math.floor((numLon + 180) / 6) + 1;
  const lonOrigin = (zoneNumber - 1) * 6 - 180 + 3;
  const radLonOrigin = (lonOrigin * Math.PI) / 180;

  const N = a / Math.sqrt(1 - e2 * Math.sin(radLat) * Math.sin(radLat));
  const T = Math.tan(radLat) * Math.tan(radLat);
  const C = ePrime2 * Math.cos(radLat) * Math.cos(radLat);
  const A = Math.cos(radLat) * (radLon - radLonOrigin);

  const M = a * (
    (1 - e2 / 4 - 3 * e2 * e2 / 64 - 5 * e2 * e2 * e2 / 256) * radLat -
    (3 * e2 / 8 + 3 * e2 * e2 / 32 + 45 * e2 * e2 * e2 / 1024) * Math.sin(2 * radLat) +
    (15 * e2 * e2 / 256 + 45 * e2 * e2 * e2 / 1024) * Math.sin(4 * radLat) -
    (35 * e2 * e2 * e2 / 3072) * Math.sin(6 * radLat)
  );

  const k0 = 0.9996;
  const easting = k0 * N * (A + (1 - T + C) * Math.pow(A, 3) / 6 + (5 - 18 * T + T * T + 72 * C - 58 * ePrime2) * Math.pow(A, 5) / 120) + 500000;
  let northing = k0 * (M + N * Math.tan(radLat) * (A * A / 2 + (5 - T + 9 * C + 4 * C * C) * Math.pow(A, 4) / 24 + (61 - 58 * T + T * T + 600 * C - 330 * ePrime2) * Math.pow(A, 6) / 720));
  if (numLat < 0) {
    northing += 10000000.0;
  }

  const bandLetters = "CDEFGHJKLMNPQRSTUVWX";
  let letter = 'N';
  if (numLat >= -80 && numLat <= 84) {
    const bandIdx = Math.floor((numLat + 80) / 8);
    letter = bandLetters[Math.min(bandIdx, bandLetters.length - 1)];
  }

  return `${zoneNumber}${letter} ${Math.round(easting)} ${Math.round(northing)}`;
}

/**
 * Format coordinates based on target format ('dd', 'dms', 'utm').
 */
export function formatCoordinates(lat, lng, format = 'dd') {
  const fmt = String(format || 'dd').toLowerCase().trim();
  if (fmt === 'dms') {
    return formatDMS(lat, lng);
  } else if (fmt === 'utm') {
    return toUTM(lat, lng);
  }
  return formatDD(lat, lng);
}

/**
 * Parse a coordinate string back to { lat, lng } numbers if possible.
 */
export function parseCoordinates(str) {
  if (!str || typeof str !== 'string') return null;
  const trimmed = str.trim();
  if (!trimmed) return null;

  // 1. Try standard DD: "-17.118579, 24.054117" or "-17.118579 24.054117"
  const ddMatch = trimmed.match(/^([+-]?\d+(?:\.\d+)?)[,\s]+([+-]?\d+(?:\.\d+)?)$/);
  if (ddMatch) {
    const lat = parseFloat(ddMatch[1]);
    const lng = parseFloat(ddMatch[2]);
    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng };
    }
  }

  // 2. Try DMS: e.g. 17°07'06.9"S, 24°03'14.8"E
  const dmsRegex = /(\d+)[°\s]+(\d+)['\s]+([\d.]+)"?\s*([NSEWnsew])/g;
  const matches = [...trimmed.matchAll(dmsRegex)];
  if (matches.length === 2) {
    let lat = null;
    let lng = null;
    for (const m of matches) {
      const deg = parseFloat(m[1]);
      const min = parseFloat(m[2]);
      const sec = parseFloat(m[3]);
      const dir = m[4].toUpperCase();
      let dec = deg + min / 60 + sec / 3600;
      if (dir === 'S' || dir === 'W') dec = -dec;
      if (dir === 'N' || dir === 'S') lat = dec;
      if (dir === 'E' || dir === 'W') lng = dec;
    }
    if (lat !== null && lng !== null && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng };
    }
  }

  return null;
}

/**
 * Determine whether a form field represents geographic coordinates.
 */
export function isCoordinateField(field) {
  if (!field) return false;
  const fType = String(field.type || '').toLowerCase();
  if (fType === 'coordinates' || fType === 'coordinate' || fType === 'location') return true;

  const vFmt = String(field.validation?.format || '').toLowerCase();
  if (vFmt === 'dd' || vFmt === 'dms' || vFmt === 'utm') return true;

  const key = String(field.key || '').toLowerCase();
  const label = String(field.label || '').toLowerCase();
  if (key.includes('coordinate') || label.includes('coordinate')) return true;
  if (key.includes('gps') || label.includes('gps')) return true;

  return false;
}
